import { Button, Typography, styled } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { agentDefaultValues, agentRules } from "./agentValidation";
import { useGetAllCountriesQuery } from "../../redux/api/countriesApi";
import { useLazyGetStatesByCountryIdQuery } from "../../redux/api/statesApi";
import { useLazyGetCitiesByProvinceIdQuery } from "../../redux/api/citiesApi";
import TextField from "../FormElements/TextField";
import AutoComplete from "../FormElements/AutoComplete";
import PhoneInput from "../FormElements/PhoneInput";
import DatePicker from "../FormElements/DatePicker";
import PasswordField from "../FormElements/PasswordField";
import { AutoCompleteMultiple } from "../FormElements/AutoCompleteMultiple";
import { LANGUAGES } from "../../constants/languages";
import { useSelector } from "react-redux";
import AgentImage from "./AgentImage";
import AgentId from "./AgentId";
import { useGetRolesQuery } from "../../redux/api/rolesApi";
import { selectCurrentAgent } from "../../redux/features/authSlice";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DocumentsSection from "./documentSection";

export default function AgentForm({
  agentData = null,
  onEditAgent,
  onAddAgent,
  isSubmitting,
}) {
  const currentAgency = useSelector(selectCurrentAgent);


  const title = useMemo(
    () => (agentData ? "Edit Staff" : "Add Staff"),
    [agentData]
  );
  const submitButtonName = useMemo(
    () => (agentData ? "Update Staff" : "Add Staff"),
    [agentData]
  );

  const methods = useForm({
    defaultValues: agentData ?? {
      ...agentDefaultValues,
      agentpassword: currentAgency?.agencypassword,
      idFile: null,
      passportFile: null,
      contractFile: null,
      idIssueDate: null,
      idExpiryDate: null,
      passportIssueDate: null,
      passportExpiryDate: null,
    },
  });

  const { handleSubmit, watch,reset, setValue, getValues } = methods;

React.useEffect(() => {
  const sub = watch((values) => {
    // console.log("values changed:", values);           // latest snapshot
    // console.log("agentimageFile:", values.agentimageFile);
  });
  return () => sub.unsubscribe();
}, [watch]);
  const watchCountryId = watch("countryid");
  const watchStateId = watch("provid");

  const { data: countries = [], isLoading: isLoadingGetAllCountries } =
    useGetAllCountriesQuery();
  const { data: roles = [], isLoading: isLoadingGetAllRole } =
    useGetRolesQuery();
  const [getStates, { data: states = [], isLoading: isLoadingGetStates }] =
    useLazyGetStatesByCountryIdQuery();
  const [getCities, { data: cities = [], isLoading: isLoadingGetCities }] =
    useLazyGetCitiesByProvinceIdQuery();

  useEffect(() => {
    if (watchCountryId) getStates(watchCountryId);
  }, [watchCountryId]);

  useEffect(() => {
    if (watchStateId) getCities(watchStateId);
  }, [watchStateId]);



  
  // When editing, map API fields -> form fields
  useEffect(() => {
    if (!agentData) return;

    reset({
      ...agentDefaultValues,
      ...agentData,

      // Convert API date strings to dayjs for pickers
      agentcareerstarting: agentData.agentcareerstarting ? dayjs(agentData.agentcareerstarting) : null,
      agentjoiningdate: agentData.agentjoiningdate ? dayjs(agentData.agentjoiningdate) : null,

      // API names -> form names
      idExpiryDate: agentData.idexpdate ? dayjs(agentData.idexpdate) : null,
      passportExpiryDate: agentData.passportexpdate ? dayjs(agentData.passportexpdate) : null,

      // If you have issuance date keys in API, map them too (use the real keys if different)
      idIssueDate: agentData.idissuedate ? dayjs(agentData.idissuedate) : null,
      passportIssueDate: agentData.passportissuedate ? dayjs(agentData.passportissuedate) : null,

      // Keep existing file URL strings so DocumentsSection can preview
      passportimg:`${agentData.passportimg}` || "",
      contractpdf: `${agentData.contractpdf}` || "",
      nicimg:`${agentData.nicimg}` || null,
      agentimage: `${agentData.agentimage}` || "",
      // passportimg:`${BASE_URL}AgentPassport/${agentData.passportimg}` || "",
      // contractpdf: `${BASE_URL}Contracts/${agentData.contractpdf}` || "",
      // nicimg:`${BASE_URL}AgentIDImages/${agentData.nicimg}` || null,
      // agentimage: `${AGENT_IMAGES_URL}/${agentData.agentimage}` || "",

      // ensure runtime files are not pre-filled
      passportFile: null,
      contractFile: null,
      idFile: null,
    });
  }, [agentData, reset]);

  const onSubmit = (data) => {
    let postData = { ...data, agencyid: currentAgency.agencyid, status: 0 };

    if (agentData) return onEditAgent(postData);
    return onAddAgent(postData);
  };

  return (
    <div>
      <Typography variant="h2">{title}</Typography>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <StyledTop>
            <Typography variant="h3" mt={4}>
              Staff Details
            </Typography>
            <AgentImage />
          </StyledTop>

          <StyledInputsContainer>
            {/* Basic Info */}
            <TextField
              name="firstName"
              label="First Name"
              // rules={agentRules.agentname}
            />
            <TextField name="lastName" label="Last Name" />
            <TextField name="agentFatherName" label="Father Name" />
            <TextField
              name="agentregno"
              label="Staff Reg No."
              // rules={agentRules.agentregno}
            />
            <TextField name="cnic" label="Nation ID No" />
             <TextField name="brokerLicense" label="Broker License" />
            <TextField name="agentExperience" label="Experience" type="number"/>

            <DatePicker
              name="agentcareerstarting"
              label="Career Start"
              // rules={agentRules.agentcareerstarting}
            />
            <DatePicker name="agentjoiningdate" label="Joining Date" />

            <PhoneInput
              name="agentphone"
              label="Phone"
              rules={agentRules.agentphone}
            />

            {/* Roles & Nationality */}
            <AutoComplete
              options={roles}
              name="roleId"
              label="Role/Designation"
              optionId="roleId"
              optionLabel="roleName"
              rules={agentRules.role}
              isLoading={isLoadingGetAllRole}
            />
            <AutoComplete
              options={countries}
              name="nationality"
              label="Nationality"
              optionId="countryid"
              optionLabel="countryname"
              // rules={agentRules.nationality}
              isLoading={isLoadingGetAllCountries}
            />
            <AutoComplete
              options={countries}
              name="countryid"
              label="Select Country"
              optionId="countryid"
              optionLabel="countryname"
              // rules={agentRules.countryid}
              isLoading={isLoadingGetAllCountries}
            />

            <AutoComplete
              options={states}
              name="provid"
              label="Select State/ Province"
              optionId="provid"
              optionLabel="provname"
              disabled={!watchCountryId}
              // rules={agentRules.provid}
              isLoading={isLoadingGetStates}
            />
            <AutoComplete
              options={cities}
              name="cityid"
              label="City Name"
              optionId="cityid"
              optionLabel="cityname"
              disabled={!watchStateId}
              // rules={agentRules.cityid}
              isLoading={isLoadingGetCities}
            />

            <TextField
              name="agentemail"
              label="Staff Email"
              rules={agentRules.agentemail}
            />
            <PasswordField
              name="agentpassword"
              label="Staff Password"
              rules={agentRules.agentpassword}
            />

            {/* <AgentId /> */}
            <AutoCompleteMultiple
              multiple
              options={LANGUAGES}
              name="agentLanguages"
              label="Select Languages"
              rules={agentRules.agentLanguages}
            />
            <TextField
              name="baseSalary"
              label="Basic Salary"
              // rules={agentRules.baseSalary}
            />
          </StyledInputsContainer>

          {/* === Document Upload Section === */}
          {/* <Typography variant="h4" mt={5}>
            Documents
          </Typography> */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DocumentsSection />
          </LocalizationProvider>

          {/* Notes */}
          <TextField
            name="agentdescription"
            label="Description"
            // rules={agentRules.agentdescription}
            multiline
            rows={4}
            sx={{ my: 3, maxWidth: "600px", display: "block" }}
          />

          <Button
            type="submit"
            variant="contained"
            sx={{ my: 2, color: "white" }}
            disabled={isSubmitting}
          >
            {submitButtonName}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
}

const StyledTop = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledInputsContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  gap: "24px",
  marginTop: "24px",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr 1fr ",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr ",
  },
}));
